import i18n from '@/utils/i18n';
import React from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { formatDate } from '@/utils/date';
import { Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  endDate: string;
  installmentAmount: string;
  installmentPeriod: string;
  isVisible: boolean;
  onClose: () => void;
  startDate: string;
  supplierName: string;
  totalAmount: string;
}

export const PaymentPlanActionSheet = ({
  endDate,
  installmentAmount,
  installmentPeriod,
  isVisible,
  onClose,
  startDate,
  supplierName,
  totalAmount,
}: Props) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  return (
    <BaseActionSheet
      isVisible={isVisible}
      onClose={onClose}
      title={i18n.t('payment_plan.action_sheet.title')}
    >
      {isVisible ? (
        <View style={styles.base}>
          {isSmallLayout ? (
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.h2, styles.titleTextStyle]}
            >
              {i18n.t('payment_plan.action_sheet.title')}
            </Text>
          ) : null}
          <Text
            style={[
              TYPOGRAPHY.actionSheetMessageText,
              styles.subtitleTextStyle,
            ]}
          >
            {i18n.t('payment_plan.action_sheet.subtitle', {
              date: formatDate(new Date(startDate)),
              supplierName,
            })}
          </Text>
          <View style={styles.infoContainer}>
            <View style={styles.infoRow}>
              <Text style={[TYPOGRAPHY.p, styles.infoName]}>
                {i18n.t('total')}
              </Text>
              <Text style={[TYPOGRAPHY.pBold, styles.infoValue]}>
                {totalAmount}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text numberOfLines={1} style={[TYPOGRAPHY.p, styles.infoName]}>
                {i18n.t('payment_plan.action_sheet.charged', {
                  period: installmentPeriod.toLowerCase(),
                })}
              </Text>
              <Text style={[TYPOGRAPHY.pBold, styles.infoValue]}>
                {installmentAmount}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text numberOfLines={1} style={[TYPOGRAPHY.p, styles.infoName]}>
                {i18n.t('payment_plan.action_sheet.estimated_end_date')}
              </Text>
              <Text style={[TYPOGRAPHY.pBold, styles.infoValue]}>
                {formatDate(new Date(endDate))}
              </Text>
            </View>
          </View>
          <Button
            onPress={onClose}
            trackingData={{ name: 'payment_plan.action_sheet.acknowledge' }}
          >
            {i18n.t('payment_plan.action_sheet.cta')}
          </Button>
        </View>
      ) : null}
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    paddingHorizontal: 12,
  },
  infoContainer: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 8,
    gap: 8,
    marginBottom: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  infoName: {
    fontSize: theme.fontSizes.small,
  },
  infoRow: {
    alignItems: 'center',
    flexDirection: 'row',
    fontSize: theme.fontSizes.xsmall,
    justifyContent: 'space-between',
  },
  infoValue: {
    fontSize: theme.fontSizes.small,
  },
  subtitleTextStyle: {
    flexWrap: 'wrap',
    paddingBottom: 16,
    textAlign: 'left',
  },
  titleTextStyle: {
    paddingBottom: 16,
  },
}));
